import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import Icon from "@material-ui/core/Icon";
import Paper from "@material-ui/core/Paper";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { useTranslation } from "react-i18next";
import ProgressButton from "../generic/ProgressButton";
import { ReactComponent as GoogleLogo } from "../../assets/google-logo.svg";
import PCGLogo from "../../assets/pcg-logo.png";
import { auth, signInWithGoogle } from "../../services/firebase";
import { PUBLIC_API_BASE_URL } from "../../config/api";

const useStyles = makeStyles({
  paper: {
    padding: "2rem",
    height: "63vh",
    width: "20rem",
    margin: "2rem auto",
    border: "2px solid #666",
  },

  logo: {
    height: "50%",
    width: "50%",
  },
});

const SignInForm = () => {
  const { t } = useTranslation("user");
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const classes = useStyles();

  const resolveEmail = async (input: string): Promise<string> => {
    if (input.includes("@")) {
      return input;
    }
    const res = await fetch(
      `${PUBLIC_API_BASE_URL}/user/resolveUsername?username=${encodeURIComponent(input)}`
    );
    if (!res.ok) {
      throw new Error("username-not-found");
    }
    const data = await res.json();
    return data.email;
  };

  const signInWithEmailAndPasswordHandler = async (
    event?: React.MouseEvent<HTMLButtonElement>
  ) => {
    if (event) {
      event.preventDefault();
    }
    setIsSubmitting(true);
    setError(null);
    try {
      const trimmed = identifier.trim();
      const email = await resolveEmail(trimmed);
      // firebase auth methods normally return a Promise, but during tests we
      // sometimes mock them to return undefined which would blow up when we call
      // .catch.  Guarding against that keeps the UI stable and makes the
      // behaviour predictable.
      const maybePromise: any = auth.signInWithEmailAndPassword(email, password);
      if (maybePromise && typeof maybePromise.catch === "function") {
        await maybePromise;
      }
    } catch (err: any) {
      if (err?.message === "username-not-found") {
        setError(t("signIn.errorUsernameNotFound"));
      } else {
        setError(t("signIn.errorInvalidCredentials"));
      }
      console.error("Error signing in", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.currentTarget;

    if (name === "userIdentifier") {
      setIdentifier(value);
    } else if (name === "userPassword") {
      setPassword(value);
    }
  };

  const onKeyUpHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const { code } = event;
    if (code === "Enter") {
      event.currentTarget.querySelector("input")?.blur();
      signInWithEmailAndPasswordHandler();
    }
  };

  return (
    <form>
      <Paper elevation={10} className={classes.paper}>
        <Grid
          container
          direction="column"
          alignItems="center"
          style={{ gap: 15 }}
        >
          <img src={PCGLogo} className={classes.logo} alt={t("signIn.logoAlt")} />
          <Typography variant="h4">{t("signIn.heading")}</Typography>
          <div style={{ minHeight: "2rem" }}>
            {error ? <Typography color="error">{error}</Typography> : null}
          </div>
          <TextField
            required
            name="userIdentifier"
            label={t("signIn.identifierLabel")}
            fullWidth
            onChange={onChangeHandler}
            placeholder={t("signIn.identifierPlaceholder")}
          />
          <TextField
            name="userPassword"
            label={t("signIn.passwordLabel")}
            fullWidth
            type="password"
            autoComplete="current-password"
            onChange={onChangeHandler}
            onKeyUp={onKeyUpHandler}
            placeholder="*****"
          />

          <ProgressButton
            type="button"
            color="primary"
            variant="contained"
            fullWidth
            size="large"
            disabled={isSubmitting}
            onClick={signInWithEmailAndPasswordHandler}
            inProgress={isSubmitting}
          >
            {t("signIn.logInButton")}
          </ProgressButton>

          <Typography>{t("signIn.orDivider")}</Typography>
          <Button
            variant="contained"
            fullWidth
            onClick={() => signInWithGoogle()}
            size="large"
            startIcon={
              <Icon fontSize="large">
                <GoogleLogo />
              </Icon>
            }
          >
            {t("signIn.signInWithGoogle")}
          </Button>
        </Grid>
      </Paper>
    </form>
  );
};
export default SignInForm;
